import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api, errMsg, money, MODE_LABEL } from "@/lib/api";
import { useSeo, track } from "@/lib/analytics";
import { Field, NativeSelect, COUNTRIES } from "./Forms";

function useQuote(cart) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const key = JSON.stringify([cart.items.map((i) => [i.product_id, i.plan_id, i.qty]), cart.currency, cart.coupon]);
  useEffect(() => {
    if (!cart.items.length) { setQuote(null); return; }
    api.post("/cart/quote", { items: cart.items, currency: cart.currency, coupon: cart.coupon || null })
      .then((r) => { setQuote(r.data); setError(""); })
      .catch((e) => { setQuote(null); setError(errMsg(e)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return { quote, error };
}

const Summary = ({ quote, children }) => (
  <aside className="card-cc h-fit space-y-3 lg:sticky lg:top-24" data-testid="order-summary">
    <p className="eyebrow">Resumen</p>
    {quote && <>
      <Row l="Subtotal" v={money(quote.subtotal, quote.currency)} id="summary-subtotal" />
      {quote.discount > 0 && <Row l={`Descuento (${quote.coupon})`} v={`− ${money(quote.discount, quote.currency)}`} id="summary-discount" />}
      <Row l={`${quote.tax_label} (${quote.tax_rate}%)`} v={money(quote.tax, quote.currency)} id="summary-tax" />
      <div className="border-t border-slate-200 pt-3"><Row l={<b>Total</b>} v={<b className="font-display text-xl">{money(quote.total, quote.currency)}</b>} id="summary-total" /></div>
    </>}
    {children}
  </aside>
);
const Row = ({ l, v, id }) => <div className="flex justify-between text-sm text-slate-600"><span>{l}</span><span data-testid={id}>{v}</span></div>;

export function CartPage() {
  const { cart, setQty, remove, setCurrency, setCoupon } = useCart();
  const { quote, error } = useQuote(cart);
  const [code, setCode] = useState(cart.coupon || "");
  useSeo({ title: "Carrito" });
  if (!cart.items.length)
    return (
      <section className="wrap py-32 text-center" data-testid="cart-empty">
        <h1 className="text-3xl font-bold">Su carrito está vacío</h1>
        <p className="lead mt-3">Explore nuestras soluciones y planes disponibles.</p>
        <Link to="/soluciones" className="btn-cc mt-8">Ver soluciones</Link>
      </section>
    );
  return (
    <section className="wrap grid gap-10 py-16 lg:grid-cols-[1.6fr_1fr]">
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Carrito</h1>
          <NativeSelect value={cart.currency} onChange={(e) => setCurrency(e.target.value)} data-testid="cart-currency" style={{ width: 110 }}>
            <option>USD</option><option>CRC</option>
          </NativeSelect>
        </div>
        <ul className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
          {cart.items.map((it, i) => {
            const line = quote?.items?.[i];
            return (
              <li key={`${it.product_id}-${it.plan_id}`} className="flex flex-wrap items-center gap-4 p-5" data-testid={`cart-item-${i}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-display font-semibold">{it.product_name}</p>
                  <p className="text-sm text-slate-500">{it.plan_name}{line && ` · ${MODE_LABEL[line.mode]}`}</p>
                </div>
                <Input type="number" min={1} value={it.qty} onChange={(e) => setQty(i, parseInt(e.target.value) || 1)} className="w-20" aria-label="Cantidad" data-testid={`cart-qty-${i}`} />
                <p className="w-28 text-right font-semibold" data-testid={`cart-line-total-${i}`}>{line ? money(line.line_total, quote.currency) : "—"}</p>
                <button onClick={() => remove(i)} aria-label="Eliminar" className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`cart-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </li>
            );
          })}
        </ul>
        {error && <p className="mt-4 flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="cart-error"><AlertTriangle className="h-4 w-4" />{error}</p>}
        <form className="mt-6 flex max-w-sm gap-2" onSubmit={(e) => { e.preventDefault(); setCoupon(code.trim()); }}>
          <Input placeholder="Código de cupón" value={code} onChange={(e) => setCode(e.target.value)} data-testid="coupon-input" />
          <button className="btn-ghost-cc border-slate-300 !py-2" data-testid="coupon-apply">Aplicar</button>
          {cart.coupon && <button type="button" onClick={() => { setCode(""); setCoupon(""); }} className="text-sm text-slate-500" data-testid="coupon-remove">Quitar</button>}
        </form>
      </div>
      <Summary quote={quote}>
        <Link to="/checkout" onClick={(e) => !quote && e.preventDefault()} className={`btn-cc mt-4 w-full ${!quote ? "pointer-events-none opacity-50" : ""}`} data-testid="cart-checkout-btn">Continuar al pago</Link>
      </Summary>
    </section>
  );
}

export function CheckoutPage() {
  const { cart } = useCart();
  const { user } = useAuth();
  const { quote, error } = useQuote(cart);
  const idem = useRef(crypto.randomUUID());
  const [b, setB] = useState({ name: user?.name || "", email: user?.email || "", company: user?.company || "", country: user?.country || "Costa Rica", tax_id: user?.tax_id || "", address: user?.address || "", phone: user?.phone || "" });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "Checkout" });
  useEffect(() => { if (quote) track("CheckoutStarted", { value: quote.total, currency: quote.currency }); }, [quote]);
  const set = (k) => (e) => setB((s) => ({ ...s, [k]: e.target.value }));
  if (!cart.items.length) return <section className="wrap py-32 text-center"><h1 className="text-3xl font-bold">No hay productos para pagar</h1><Link to="/soluciones" className="btn-cc mt-6">Ver soluciones</Link></section>;
  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error("Debe aceptar las condiciones comerciales");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", { items: cart.items, currency: cart.currency, coupon: cart.coupon || null, billing: b, origin_url: window.location.origin, accept_terms: terms },
        { headers: { "Idempotency-Key": idem.current } });
      window.location.assign(data.redirect_url);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };
  return (
    <section className="wrap grid gap-10 py-16 lg:grid-cols-[1.6fr_1fr]">
      <form onSubmit={submit} className="card-cc grid gap-5 sm:grid-cols-2" data-testid="checkout-form">
        <div className="sm:col-span-2"><h1 className="text-3xl font-bold">Datos de facturación</h1><p className="mt-1 text-sm text-slate-500">Compras personales o empresariales.</p></div>
        <Field label="Nombre completo" required><Input required value={b.name} onChange={set("name")} data-testid="billing-name" /></Field>
        <Field label="Correo electrónico" required><Input required type="email" value={b.email} onChange={set("email")} data-testid="billing-email" /></Field>
        <Field label="Empresa"><Input value={b.company} onChange={set("company")} data-testid="billing-company" /></Field>
        <Field label="País" required><NativeSelect value={b.country} onChange={set("country")} data-testid="billing-country">{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</NativeSelect></Field>
        <Field label="Identificación tributaria (cédula física/jurídica)"><Input value={b.tax_id} onChange={set("tax_id")} data-testid="billing-tax-id" /></Field>
        <Field label="Teléfono"><Input value={b.phone} onChange={set("phone")} data-testid="billing-phone" /></Field>
        <div className="sm:col-span-2"><Field label="Dirección de facturación"><Input value={b.address} onChange={set("address")} data-testid="billing-address" /></Field></div>
        <label className="sm:col-span-2 flex items-start gap-3 text-sm text-slate-600">
          <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="checkout-terms" className="mt-0.5" />
          <span>Acepto las <Link to="/legal/commercial" className="text-brand underline">condiciones comerciales</Link> y los <Link to="/legal/terms" className="text-brand underline">términos</Link>.</span>
        </label>
        <p className="sm:col-span-2 flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" />El pago se procesa en la pasarela del proveedor. Cloud Center no almacena datos de tarjetas.</p>
        {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
        <button disabled={busy || !quote} className="btn-cc sm:col-span-2 disabled:opacity-60" data-testid="checkout-submit">{busy ? "Procesando…" : `Pagar ${quote ? money(quote.total, quote.currency) : ""}`}</button>
      </form>
      <Summary quote={quote}>
        <ul className="space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-500">{cart.items.map((i) => <li key={i.plan_id}>{i.qty} × {i.product_name} · {i.plan_name}</li>)}</ul>
      </Summary>
    </section>
  );
}

export function PaymentMock() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/portal/session/${sid}`).then((r) => setOrder(r.data)).catch((e) => toast.error(errMsg(e))); }, [sid]);
  const confirm = async (outcome) => {
    setBusy(true);
    try {
      await api.post("/payments/mock/confirm", { session_id: sid, outcome });
      navigate(`/pago/resultado?session_id=${sid}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <section className="wrap max-w-xl py-20">
      <div className="card-cc" data-testid="mock-gateway">
        <p className="rounded-lg bg-amber-100 px-3 py-2 font-mono text-[11px] uppercase tracking-widest text-amber-800">Pasarela de pago SIMULADA · entorno de pruebas</p>
        <h1 className="mt-6 text-2xl font-bold">Confirmar pago</h1>
        {order ? <>
          <p className="mt-2 text-sm text-slate-500">Pedido {order.number}</p>
          <p className="mt-6 font-display text-4xl font-bold" data-testid="mock-amount">{money(order.total, order.currency)}</p>
          <p className="mt-6 text-sm text-slate-500">Al integrar la pasarela definitiva (p. ej. Tilopay, BAC Credomatic u otra), esta pantalla será reemplazada por el formulario seguro del proveedor.</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <button disabled={busy} onClick={() => confirm("approve")} className="btn-cc" data-testid="mock-approve">Aprobar pago</button>
            <button disabled={busy} onClick={() => confirm("decline")} className="btn-ghost-cc border-slate-300" data-testid="mock-decline">Rechazar pago</button>
          </div>
        </> : <Loader2 className="mt-6 h-6 w-6 animate-spin text-slate-400" />}
      </div>
    </section>
  );
}

export function PaymentResult() {
  const [sp] = useSearchParams();
  const sid = sp.get("session_id");
  const orderId = sp.get("order");
  const { clear } = useCart();
  const [status, setStatus] = useState(orderId ? "paid" : "pending");
  useEffect(() => {
    if (!sid) return;
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (stop) return;
        if (data.payment_status === "paid" || data.payment_status === "failed") return setStatus(data.payment_status);
      } catch { /* retry */ }
      if (++n < 10 && !stop) setTimeout(poll, 2000); else if (!stop) setStatus("timeout");
    };
    poll();
    return () => { stop = true; };
  }, [sid]);
  const paidOnce = useRef(false);
  useEffect(() => {
    if (status === "paid" && !paidOnce.current) { paidOnce.current = true; clear(); track("PurchaseCompleted", { session: sid }); }
  }, [status, clear, sid]);
  const view = useMemo(() => ({
    pending: [Loader2, "Confirmando su pago…", "Esperando la confirmación del proveedor de pagos.", "text-slate-400 animate-spin"],
    paid: [CheckCircle2, "¡Pago confirmado!", "Sus licencias y suscripciones ya están activas en el portal de clientes.", "text-emerald-500"],
    failed: [XCircle, "El pago no fue aprobado", "Puede intentarlo nuevamente desde el carrito.", "text-red-500"],
    timeout: [AlertTriangle, "Confirmación pendiente", "Aún no recibimos la confirmación. Revise su portal en unos minutos.", "text-amber-500"],
  })[status], [status]);
  const [Icon, title, text, cls] = view;
  return (
    <section className="wrap max-w-xl py-24 text-center" data-testid={`payment-result-${status}`}>
      <Icon className={`mx-auto h-14 w-14 ${cls}`} />
      <h1 className="mt-6 text-3xl font-bold">{title}</h1>
      <p className="lead mt-3">{text}</p>
      <div className="mt-8 flex justify-center gap-3">
        <Link to="/portal" className="btn-cc" data-testid="result-portal-btn">Ir al portal</Link>
        {status === "failed" && <Link to="/carrito" className="btn-ghost-cc border-slate-300">Volver al carrito</Link>}
      </div>
    </section>
  );
}
